import React, { useState, useEffect } from 'react';
import WorkspaceList from '../components/features/workspace/WorkspaceList/WorkspaceList';
import DataFileUpload from '../components/features/data/upload/DataFileUpload';
import DataUrlUpload from '../components/features/data/upload/DataUrlUpload';
import DataTextUpload from '../components/features/data/upload/DataTextUpload';
import DataList from '../components/features/data/DataList/DataList';
import axios from '../api/myAxios';
import PreviewWindow from '../components/features/workspace/PreviewWindow/PreviewWindow';
import {
  Container,
  Box,
  Typography,
  Grid2,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTranslation } from 'react-i18next';
import Analytics from '../components/features/analytics/Analytics';
import { usePageTitle } from '../hooks/usePageTitle';

function Dashboard() {
  const { t } = useTranslation();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [uploadType, setUploadType] = useState('null');
  const [refetch, setRefetch] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState(-1);
  const [currentView, setCurrentView] = useState('workspace');
  const [editingItem, setEditingItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  usePageTitle('Dashboard');

  useEffect(() => {
    if (activeWorkspace === -1) {
      setSelectedWorkspace(null);
    }
  }, [activeWorkspace]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setError(null);
        // 1. Récupérer le workspace à partir du sous-domaine
        const workspaceResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/public/user-by-workspace-subdomain/${selectedWorkspace.subdomain}`
        );
        const workspaceUserId = workspaceResponse.data.userId;

        // 2. Récupérer l'URL de la photo de profil
        const photoResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/public/user-photo/${workspaceUserId}`
        );
        setUserPhoto(photoResponse.data.photoURL);
      } catch (error) {
        console.error(t('error.console.errorFetchingUserData'), error);
        setError(t('dashboard.unableToLoadProfile'));
      }
    };
    if (selectedWorkspace && selectedWorkspace.subdomain) {
      fetchUserData();
    }
  }, [selectedWorkspace, t]);

  const handleWorkspaceSelect = (workspace) => {
    setSelectedWorkspace({ ...workspace });
  };

  const handleUploadSuccess = () => {
    setTimeout(() => {
      setOpenDialog(false);
      setIsEditMode(false);
      setEditingItem(null);
    }, 1500);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setIsEditMode(true);
    setUploadType(item.type);
    setOpenDialog(true);
  };

  const handleNewUpload = (type) => {
    setEditingItem(null);
    setIsEditMode(false);
    setUploadType(type);
    setOpenDialog(true);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          mb: 4,
          width: '100%',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          gutterBottom
          color="primary"
        >
          {t('dashboard.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('dashboard.description')}
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid2
        container
        spacing={2}
        sx={{ flexGrow: 1, overflow: 'hidden', width: '100%' }}
      >
        <Grid2
          size={selectedWorkspace ? { xs: 12, md: 6 } : 12}
          sx={{ flexGrow: 1, height: '100%' }}
        >
          <WorkspaceList
            onSelect={handleWorkspaceSelect}
            activeWorkspace={activeWorkspace}
            setActiveWorkspace={setActiveWorkspace}
          />
        </Grid2>
        {selectedWorkspace && (
          <Grid2
            size={{ xs: 12, md: 6 }}
            sx={{ flexGrow: 1, height: '100%', width: '100%' }}
          >
            <Paper sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                <Typography variant="h5" sx={{ mb: 2, width: '100%' }}>
                  {t('dashboard.workspace')}: {selectedWorkspace.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleNewUpload('file')}
                    fullWidth
                  >
                    {t('dashboard.file')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    onClick={() => handleNewUpload('url')}
                    fullWidth
                  >
                    {t('dashboard.url')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TextFieldsIcon />}
                    onClick={() => handleNewUpload('text')}
                    fullWidth
                  >
                    {t('dashboard.text')}
                  </Button>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    fullWidth
                    maxWidth="sm"
                    slotProps={{ paper: { sx: { borderRadius: 4 } } }}
                  >
                    <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <DialogActions>
                        <IconButton
                          onClick={() => setOpenDialog(false)}
                          color="primary"
                          size="large"
                          aria-label="close dialog"
                        >
                          <CloseIcon />
                        </IconButton>
                      </DialogActions>
                      {uploadType === 'file' && (
                        <DataFileUpload
                          workspaceId={selectedWorkspace.id}
                          onSuccess={handleUploadSuccess}
                          isEditMode={isEditMode}
                          editingItem={editingItem}
                        />
                      )}
                      {uploadType === 'url' && (
                        <DataUrlUpload
                          workspaceId={selectedWorkspace.id}
                          onSuccess={handleUploadSuccess}
                          isEditMode={isEditMode}
                          editingItem={editingItem}
                        />
                      )}
                      {uploadType === 'text' && (
                        <DataTextUpload
                          workspaceId={selectedWorkspace.id}
                          onSuccess={handleUploadSuccess}
                          isEditMode={isEditMode}
                          editingItem={editingItem}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                  <DataList
                    workspaceId={selectedWorkspace.id}
                    refetch={refetch}
                    onEditItem={handleEditItem}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid2>
        )}
      </Grid2>
      <Box sx={{ mt: 3 }}>
        {selectedWorkspace && (
          <>
            <Box sx={{ mb: 3 }}>
              <Button
                variant={currentView === 'workspace' ? 'contained' : 'outlined'}
                onClick={() => setCurrentView('workspace')}
                // sx={{ mr: 2 }}
                sx={{ mr: 1 }}
              >
                {t('dashboard.preview')}
              </Button>
              <Button
                variant={currentView === 'analytics' ? 'contained' : 'outlined'}
                onClick={() => setCurrentView('analytics')}
                disabled={!selectedWorkspace}
              >
                {t('dashboard.analytics')}
              </Button>
            </Box>
            {currentView === 'workspace' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<LaunchIcon />}
                    onClick={() => {
                      console.log('NODE_ENV:', process.env.NODE_ENV);
                      console.log(
                        'REACT_APP_SUBDOMAIN_PATTERN:',
                        process.env.REACT_APP_SUBDOMAIN_PATTERN
                      );
                      console.log(
                        'All env vars:',
                        Object.keys(process.env).filter((key) =>
                          key.startsWith('REACT_APP')
                        )
                      );
                      const pattern =
                        process.env.REACT_APP_SUBDOMAIN_PATTERN ||
                        `https://{subdomain}.get-to-know.me`;
                      const url = pattern.replace(
                        '{subdomain}',
                        selectedWorkspace.subdomain
                      );
                      // const url = `${process.env.REACT_APP_FRONTEND_URL}/${selectedWorkspace.subdomain}`;
                      // const url = `https://${selectedWorkspace.subdomain}.localhost:3000`;
                      window.open(url, '_blank');
                    }}
                    sx={{
                      px: 3,
                      py: 1,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    {t('dashboard.viewPersonalizedPage')}
                  </Button>
                </Box>
                <PreviewWindow
                  userPhoto={userPhoto}
                  selectedWorkspace={selectedWorkspace}
                />
              </>
            ) : (
              <Analytics
                selectedWorkspace={selectedWorkspace}
                sx={{ p: 0, m: 0 }}
              />
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default Dashboard;
